import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Body1, ButtonText, Subtitle } from "@iventis/styles/src/components/texts";
import { Checkbox, useTheme } from "@mui/material";
import { CONTACT_URL, isAdmin } from "@iventis/utilities";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@iventis/styles";
import { CheckedCheckBox, UncheckedCheckBox } from "@iventis/styles/src/components/checkbox-elements";
import { Content } from "@iventis/translations/content/typed-content";
import { Theme } from "@emotion/react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { findNode, TreeBrowserDomainNodeContext, useTreeBrowser } from "@iventis/tree-browser";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";
import { getSitemapMapOrderValue, TagComponent } from "@iventis/sitemaps";
import { RootDispatch } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";
import { SidebarState } from "./state/sidebar.slice.types";
import { updateMap } from "./state/map.slice.thunks";
import { allExistingSitemapTypesVisible, hasSitemapVersionLevelsOfType, onSitemapSelected, someSitemapTypesVisible } from "./sitemap-selector-helpers";
import { SitemapVersionSelectorComponent } from "./sitemap-version-selector";

export const SitemapSelectorComponent = () => {
    const dispatch = useDispatch<RootDispatch>();
    const { sitemaps, sitemapConfigs, mapId } = useSelector(({ mapReducer }: { mapReducer: MapGlobalState }) => ({
        // Only show available sitemaps and the sitemap configs for that map
        sitemaps: mapReducer.sitemapsInView,
        sitemapConfigs: mapReducer.sitemapConfigs,
        mapId: mapReducer.mapModule.mapId,
    }));

    const [{ mainNodeId, tree }] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "mainNodeId", "tree");

    const mapNode = useMemo(() => tree && findNode(tree?.childNodes, mainNodeId), [mainNodeId, tree]);
    const adminOnMapNode = isAdmin(mapNode?.authorisation);

    const { fullScreenSelector } = useSelector(({ sidebarReducer }: { sidebarReducer: SidebarState }) => sidebarReducer);
    const handleSelectDeselectSitemap = (e: React.ChangeEvent<HTMLInputElement>, sitemapId: string) => {
        const updatedMap = onSitemapSelected(mapId, sitemaps, sitemapConfigs, sitemapId, e.target.checked);
        dispatch(updateMap({ map: updatedMap, skipSave: !adminOnMapNode }));
    };

    const translate = useIventisTranslate();
    const theme = useTheme<Theme>();

    // Sort the sitemaps so they are the same order as they appear in the map. We want the sitemaps at the top of the map at the top of the sitemap selector
    const orderedSitemaps = [...sitemaps].sort((a, b) => getSitemapMapOrderValue(b.mapOrder) - getSitemapMapOrderValue(a.mapOrder));

    return (
        <StyledSelectorPopover>
            <Subtitle className="title">{translate(Content.map.popup.sitemapsAvailableAtThisLocation)}</Subtitle>
            {fullScreenSelector && (
                <StyledCloseButton onClick={() => dispatch(sidebar.actions.setFullScreenSelector(null))}>
                    <FontAwesomeIcon icon="xmark" />
                    <span className="hidden">{translate(Content.map.popup.closeSitemapSelector)}</span>
                </StyledCloseButton>
            )}
            {sitemaps.length === 0 && (
                <Body1>
                    {translate(Content.map3.sitemaps.no_sitemaps_available)}{" "}
                    <StyledAnchor target="_blank" rel="noopener noreferrer" href={CONTACT_URL}>
                        {translate(Content.login.footer.contact)}
                    </StyledAnchor>{" "}
                    {translate(Content.map3.sitemaps.no_sitemaps_available_2)}
                </Body1>
            )}
            {sitemaps.length > 0 && (
                <StyledSitemapList>
                    {orderedSitemaps.map((sitemap) => (
                        <div className="sitemap-option" key={sitemap.id}>
                            <label htmlFor={sitemap.id}>
                                <Checkbox
                                    checked={allExistingSitemapTypesVisible(sitemap, sitemapConfigs)}
                                    indeterminate={!allExistingSitemapTypesVisible(sitemap, sitemapConfigs) && someSitemapTypesVisible(sitemap, sitemapConfigs)}
                                    icon={<UncheckedCheckBox size={15} borderStyle={`1px solid ${theme.typographyColors.subdued}`} backgroundColour="none" />}
                                    checkedIcon={
                                        <CheckedCheckBox
                                            size={15}
                                            tickColour={theme.shades.two}
                                            borderStyle={`1px solid ${theme.typographyColors.subdued}`}
                                            backgroundColour="none"
                                        />
                                    }
                                    name={sitemap.name}
                                    id={sitemap.id}
                                    color="default"
                                    onChange={(e) => {
                                        handleSelectDeselectSitemap(e, sitemap.id);
                                    }}
                                    data-testid={`${sitemap.name}-checkbox-sitemap`}
                                />
                                <ButtonText style={{ display: "inline-block", marginRight: "5px" }}>{sitemap.name}</ButtonText>
                                <TagComponent tag={sitemap.tag} backgroundColour={sitemap.colour} />
                            </label>

                            {/* 2D Sitemaps */}
                            {hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.TwoDimensional) && (
                                <SitemapVersionSelectorComponent sitemapId={sitemap.id} versionType={SitemapVersionType.TwoDimensional} />
                            )}

                            {/* 3D Sitemaps */}
                            {hasSitemapVersionLevelsOfType(sitemap, SitemapVersionType.ThreeDimensional) && (
                                <SitemapVersionSelectorComponent sitemapId={sitemap.id} versionType={SitemapVersionType.ThreeDimensional} />
                            )}
                        </div>
                    ))}
                </StyledSitemapList>
            )}
        </StyledSelectorPopover>
    );
};

export const StyledSelectorPopover = styled.div`
    position: relative;
    padding: 15px;
    display: flex;
    flex-direction: column;
    .title {
        margin-bottom: 10px;
    }
`;

const StyledSitemapList = styled.div`
    .sitemap-option {
        display: flex;
        flex-direction: column;

        .MuiRadio-root {
            margin-left: 35px;
        }
        .Mui-checked {
            color: inherit;
            opacity: 0.75;
        }
    }
`;

const StyledCloseButton = styled(InteractiveElement)`
    position: absolute;
    top: 10px;
    right: 10px;
`;

const StyledAnchor = styled.a`
    text-transform: lowercase;
`;
